import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toPng } from "html-to-image";
import hoCardBackground from "./assets/ho-card-background.png";
import saleCardBackground from "./assets/blue-tien-phong.png";
import "./style.css";

const CAMPAIGN_DATE = new Date(2026, 6, 15);
const MIN_JOIN_DATE = "2019-07-15";
const CROP_FRAME_SIZE = 320;

function toDateInputValue(value) {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function normalizeJoinDate(value) {
    const dateValue = toDateInputValue(value);
    if (!dateValue) return "";

    return dateValue < MIN_JOIN_DATE ? MIN_JOIN_DATE : dateValue;
}

function isMobileDevice() {
    if (typeof navigator === "undefined") return false;

    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isSafariBrowser() {
    if (typeof navigator === "undefined") return false;

    const userAgent = navigator.userAgent;
    return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/i.test(userAgent);
}

function getSyncDays(joinDate) {
    if (!joinDate) return 0;

    const start = new Date(`${normalizeJoinDate(joinDate)}T00:00:00`);
    if (Number.isNaN(start.getTime())) return 0;

    const days = Math.round((CAMPAIGN_DATE - start) / 86400000);
    return Math.max(days, 0);
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function getCropGeometry(meta, zoom, offset) {
    if (!meta.width || !meta.height) {
        return {
            drawWidth: CROP_FRAME_SIZE,
            drawHeight: CROP_FRAME_SIZE,
            left: 0,
            top: 0,
            scale: 1,
        };
    }

    const baseScale = Math.max(CROP_FRAME_SIZE / meta.width, CROP_FRAME_SIZE / meta.height);
    const scale = baseScale * zoom;
    const drawWidth = meta.width * scale;
    const drawHeight = meta.height * scale;

    return {
        drawWidth,
        drawHeight,
        left: (CROP_FRAME_SIZE - drawWidth) / 2 + offset.x,
        top: (CROP_FRAME_SIZE - drawHeight) / 2 + offset.y,
        scale,
    };
}

function clampCropOffset(meta, zoom, offset) {
    const geometry = getCropGeometry(meta, zoom, { x: 0, y: 0 });
    const maxX = Math.max(0, (geometry.drawWidth - CROP_FRAME_SIZE) / 2);
    const maxY = Math.max(0, (geometry.drawHeight - CROP_FRAME_SIZE) / 2);

    return {
        x: clamp(offset.x, -maxX, maxX),
        y: clamp(offset.y, -maxY, maxY),
    };
}

function revokeObjectUrl(url) {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
}

function loadExportImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = reject;
        if (isCrossOriginUrl(src)) image.crossOrigin = "anonymous";
        image.src = src;
    });
}

function drawCoverImage(context, image, x, y, size) {
    const scale = Math.max(size / image.naturalWidth, size / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    context.drawImage(image, x + (size - width) / 2, y + (size - height) / 2, width, height);
}

async function exportSafariCard({ cardElement, background, photoUrl }) {
    const size = 1500;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext("2d");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    const [backgroundImage, avatarImage] = await Promise.all([
        loadExportImage(background),
        photoUrl ? loadExportImage(photoUrl).catch(() => null) : Promise.resolve(null),
    ]);

    const avatarX = size * 0.2907;
    const avatarY = size * 0.2613;
    const avatarSize = size * 0.3773;
    context.save();
    context.beginPath();
    context.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
    context.clip();
    context.fillStyle = "#dbe3ed";
    context.fillRect(avatarX, avatarY, avatarSize, avatarSize);
    if (avatarImage) drawCoverImage(context, avatarImage, avatarX, avatarY, avatarSize);
    context.restore();

    // Thứ tự này phải trùng preview: avatar nằm dưới template, để dải lụa
    // trong ảnh nền phủ lên phần chân dung trước khi vẽ họ tên/chức danh.
    context.drawImage(backgroundImage, 0, 0, size, size);

    // Safari không giữ lại ảnh nền/avatar khi html-to-image render, nhưng phần
    // chữ vẫn chính xác. Ghép lớp chữ DOM lên nền và avatar Canvas để vị trí
    // khớp tuyệt đối với preview, kể cả nội dung xuống dòng.
    const textOverlayUrl = await toPng(cardElement, {
        cacheBust: true,
        canvasWidth: size,
        canvasHeight: size,
        pixelRatio: 1,
    });
    const textOverlay = await loadExportImage(textOverlayUrl);
    context.drawImage(textOverlay, 0, 0, size, size);

    return canvas.toDataURL("image/png");
}

async function createImageBlobUrl(dataUrl) {
    const response = await fetch(dataUrl);
    const imageBlob = await response.blob();
    return URL.createObjectURL(imageBlob);
}

function getAvatarUrl(userData) {
    return userData.avatar || "";
}

function getUserFormData(userData) {
    return {
        fullname: userData.fullname || "",
        title: userData.chucvu || userData.chucvu_title || userData.position_title || "",
        joinDate: normalizeJoinDate(userData.datejoining),
    };
}

function getSizedAvatarUrl(url) {
    if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
    if (url.includes("size=avatar")) return url;

    return `${url}${url.includes("?") ? "&" : "?"}size=avatar`;
}

function isCrossOriginUrl(url) {
    if (!url || url.startsWith("data:") || url.startsWith("blob:")) return false;

    try {
        return new URL(url, window.location.href).origin !== window.location.origin;
    } catch (error) {
        return false;
    }
}

export default function ChucMungSinhNhatBHS() {
    const { user } = useSelector((state) => state.auth);
    const userData = user && user.data ? user.data : {};
    const cardRef = useRef(null);
    const photoInputRef = useRef(null);
    const cropSourceUrlRef = useRef("");
    const saveImageUrlRef = useRef("");
    const cropDragRef = useRef(null);
    const customPhotoRef = useRef("");
    const hasEditedFormRef = useRef(false);

    const [khoi, setKhoi] = useState("HO");
    const [formData, setFormData] = useState(() => getUserFormData(userData));
    const [photoUrl, setPhotoUrl] = useState("");
    const [hasCustomPhoto, setHasCustomPhoto] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState("");
    const [cropMeta, setCropMeta] = useState({ width: 0, height: 0 });
    const [cropZoom, setCropZoom] = useState(1);
    const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [saveImageUrl, setSaveImageUrl] = useState("");

    const avatarUrl = getSizedAvatarUrl(getAvatarUrl(userData));
    const syncDays = getSyncDays(formData.joinDate).toLocaleString("vi-VN");
    const background = khoi === "SALE" ? saleCardBackground : hoCardBackground;
    const isSafariExporting = isExporting && isSafariBrowser();
    const cropGeometry = getCropGeometry(cropMeta, cropZoom, cropOffset);
    const cropFrameStyle = cropSourceUrl ? {
        backgroundImage: `url("${cropSourceUrl}")`,
        backgroundSize: `${cropGeometry.drawWidth}px ${cropGeometry.drawHeight}px`,
        backgroundPosition: `${cropGeometry.left}px ${cropGeometry.top}px`,
    } : {};

    useEffect(() => {
        if (!hasCustomPhoto) {
            setPhotoUrl(avatarUrl);
        }
    }, [avatarUrl, hasCustomPhoto]);

    useEffect(() => {
        if (!hasEditedFormRef.current) {
            setFormData(getUserFormData(userData));
        }
    }, [userData.fullname, userData.chucvu, userData.chucvu_title, userData.position_title, userData.datejoining]);

    useEffect(() => () => {
        revokeObjectUrl(cropSourceUrlRef.current);
        revokeObjectUrl(saveImageUrlRef.current);
    }, []);

    const updateFormData = (field) => (event) => {
        hasEditedFormRef.current = true;
        const value = field === "joinDate" ? normalizeJoinDate(event.target.value) : event.target.value;
        setFormData((current) => ({ ...current, [field]: value }));
    };

    const closeSaveImage = () => {
        setSaveImageUrl("");
    };

    const openSaveImageInNewTab = () => {
        if (!saveImageUrl) return;

        // Phải gọi trực tiếp trong sự kiện click để Safari (macOS/iOS) coi đây là
        // thao tác mở tab của người dùng, thay vì một popup bị chặn.
        const imageTab = window.open(saveImageUrl, "_blank");

        // Chỉ dùng trang hiện tại làm phương án dự phòng khi người dùng bật chặn
        // popup ở cấp trình duyệt.
        if (!imageTab) window.location.assign(saveImageUrl);
    };

    const closeCropper = () => {
        setIsCropOpen(false);
        setCropSourceUrl("");
        setCropMeta({ width: 0, height: 0 });
        setCropOffset({ x: 0, y: 0 });
        setCropZoom(1);

        revokeObjectUrl(cropSourceUrlRef.current);
        cropSourceUrlRef.current = "";

        if (photoInputRef.current) {
            photoInputRef.current.value = "";
        }
    };

    const openCropper = (sourceUrl) => {
        if (!sourceUrl) return;

        const image = new Image();

        image.onload = () => {
            revokeObjectUrl(cropSourceUrlRef.current);
            cropSourceUrlRef.current = sourceUrl.startsWith("blob:") ? sourceUrl : "";
            setCropSourceUrl(sourceUrl);
            setCropMeta({ width: image.naturalWidth, height: image.naturalHeight });
            setCropOffset({ x: 0, y: 0 });
            setCropZoom(1);
            setIsCropOpen(true);
        };

        image.onerror = () => {
            revokeObjectUrl(sourceUrl);
            window.alert("Không thể đọc ảnh này. Vui lòng chọn ảnh khác.");
        };

        if (isCrossOriginUrl(sourceUrl)) image.crossOrigin = "anonymous";
        image.src = sourceUrl;
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        openCropper(URL.createObjectURL(file));
    };

    const editCurrentPhoto = () => {
        if (!photoUrl) return;
        openCropper(photoUrl);
    };

    const updateCropZoom = (event) => {
        const nextZoom = Number(event.target.value);
        setCropZoom(nextZoom);
        setCropOffset((current) => clampCropOffset(cropMeta, nextZoom, current));
    };

    const startCropDrag = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        cropDragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            offset: cropOffset,
        };
    };

    const moveCropDrag = (event) => {
        if (!cropDragRef.current || cropDragRef.current.pointerId !== event.pointerId) return;

        const nextOffset = {
            x: cropDragRef.current.offset.x + event.clientX - cropDragRef.current.startX,
            y: cropDragRef.current.offset.y + event.clientY - cropDragRef.current.startY,
        };

        setCropOffset(clampCropOffset(cropMeta, cropZoom, nextOffset));
    };

    const stopCropDrag = (event) => {
        if (!cropDragRef.current || cropDragRef.current.pointerId !== event.pointerId) return;
        cropDragRef.current = null;
    };

    const applyCrop = () => {
        if (!cropSourceUrl) return;

        const image = new Image();

        image.onload = () => {
            const canvas = document.createElement("canvas");
            const outputSize = 1000;
            const geometry = getCropGeometry(cropMeta, cropZoom, cropOffset);
            const context = canvas.getContext("2d");
            const sourceX = Math.max(0, -geometry.left / geometry.scale);
            const sourceY = Math.max(0, -geometry.top / geometry.scale);
            const sourceSize = CROP_FRAME_SIZE / geometry.scale;

            canvas.width = outputSize;
            canvas.height = outputSize;
            try {
                context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize);
                revokeObjectUrl(customPhotoRef.current);
                const nextPhotoUrl = canvas.toDataURL("image/png");
                customPhotoRef.current = nextPhotoUrl;
                setPhotoUrl(nextPhotoUrl);
                setHasCustomPhoto(true);
                closeCropper();
            } catch (error) {
                console.error("Không thể crop ảnh avatar", error);
                window.alert("Không thể sửa ảnh avatar này. Vui lòng tải ảnh cá nhân từ máy để crop lại.");
            }
        };

        image.onerror = () => {
            window.alert("Không thể crop ảnh này. Vui lòng chọn ảnh khác.");
        };

        if (isCrossOriginUrl(cropSourceUrl)) image.crossOrigin = "anonymous";
        image.src = cropSourceUrl;
    };

    const downloadCard = async () => {
        if (!cardRef.current || isDownloading) return;

        setIsDownloading(true);
        setIsExporting(true);
        try {
            await document.fonts.ready;
            await new Promise((resolve) => window.requestAnimationFrame(resolve));
            const dataUrl = isSafariBrowser()
                ? await exportSafariCard({
                    cardElement: cardRef.current,
                    background,
                    photoUrl,
                })
                : await toPng(cardRef.current, {
                    cacheBust: true,
                    canvasWidth: 1500,
                    canvasHeight: 1500,
                    pixelRatio: 1,
                });
            const safeName = (formData.fullname || "thiep-tri-an")
                .trim()
                .replace(/\s+/g, "-")
                .replace(/^-|-$/g, "")
                .toLowerCase() || "thiep-tri-an";
            const fileName = `${safeName}-bhs.png`;

            if (isMobileDevice()) {
                // iOS Safari không mở ổn định data: URL lớn ở tab mới. Blob URL
                // được mở từ một lần chạm trực tiếp trong hộp xác nhận bên dưới.
                const imageUrl = await createImageBlobUrl(dataUrl);
                revokeObjectUrl(saveImageUrlRef.current);
                saveImageUrlRef.current = imageUrl;
                setSaveImageUrl(imageUrl);
                return;
            }

            const link = document.createElement("a");
            link.download = fileName;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            if (error && error.name === "AbortError") return;

            console.error("Không thể xuất thiệp", error);
            window.alert("Không thể tải ảnh thiệp. Vui lòng thử lại.");
        } finally {
            setIsExporting(false);
            setIsDownloading(false);
        }
    };

    return (
        <div className="birthday-card-page">
            <div className="birthday-card-app">
                <div className="birthday-card-heading">
                    <div className="birthday-card-eyebrow">BHS GROUP · KHỐI {khoi}</div>
                    <h1>Thiệp Tri Ân Blue — 7 Năm Đồng Nhịp</h1>
                    <p>Tạo thiệp tri ân của riêng bạn</p>
                </div>

                <div className="birthday-card-layout">
                    <section className="birthday-card-controls" aria-label="Thông tin thiệp">
                        <div>
                            <label>Khối</label>
                            <div className="birthday-card-khoi-buttons">
                                <button type="button" className={khoi === "HO" ? "is-active" : ""} onClick={() => setKhoi("HO")}>Khối HO</button>
                                <button type="button" className={khoi === "SALE" ? "is-active" : ""} onClick={() => setKhoi("SALE")}>Khối SALE</button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="birthday-card-fullname">Họ và tên</label>
                            <input id="birthday-card-fullname" value={formData.fullname} onChange={updateFormData("fullname")} placeholder="Nhập họ và tên" />
                        </div>

                        <div>
                            <label htmlFor="birthday-card-title">Chức danh</label>
                            <input id="birthday-card-title" value={formData.title} onChange={updateFormData("title")} placeholder="Nhập chức danh" />
                        </div>

                        <div>
                            <label htmlFor="birthday-card-join-date">Ngày gia nhập công ty</label>
                            <input id="birthday-card-join-date" type="date" min={MIN_JOIN_DATE} value={formData.joinDate} onChange={updateFormData("joinDate")} />
                        </div>

                        <button type="button" className="birthday-card-download-button" onClick={downloadCard} disabled={isDownloading}>
                            {isDownloading && <span className="birthday-card-button-spinner" aria-hidden="true" />}
                            <span>{isDownloading ? "Đang tạo ảnh..." : "Tải về thiệp PNG"}</span>
                        </button>
                    </section>

                    <div className="birthday-card-preview-wrap">
                        <div className={`birthday-card-preview${isExporting ? " is-exporting" : ""}${isSafariExporting ? " is-safari-exporting" : ""}`} ref={cardRef}>
                            <input ref={photoInputRef} className="birthday-card-file-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePhotoChange} />
                            <div className="birthday-card-avatar">
                                <button
                                    type="button"
                                    className={photoUrl ? "birthday-card-avatar-picker has-photo" : "birthday-card-avatar-picker"}
                                    onClick={() => photoInputRef.current && photoInputRef.current.click()}
                                    title={photoUrl ? "Thay ảnh cá nhân" : "Tải ảnh cá nhân"}
                                >
                                    {photoUrl ? (
                                        <img src={photoUrl} alt="Ảnh cá nhân" />
                                    ) : null}
                                    <span className="birthday-card-avatar-upload-icon">
                                        <span className="fas fa-upload" />
                                    </span>
                                </button>
                                {photoUrl && (
                                    <button type="button" className="birthday-card-avatar-edit" onClick={editCurrentPhoto} title="Sửa crop ảnh">
                                        <span className="fas fa-pencil-alt" />
                                    </button>
                                )}
                            </div>
                            <img className="birthday-card-background" src={background} alt="" />
                            <div className="birthday-card-copy">
                                <div className="birthday-card-person-pill">
                                    <span className="birthday-card-person-name">{formData.fullname || "HỌ VÀ TÊN"}</span>
                                    <span className="birthday-card-person-title">{formData.title || "CHỨC DANH"}</span>
                                </div>
                                <div className="birthday-card-days">{syncDays}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {saveImageUrl && (
                    <div className="birthday-card-save-modal" role="dialog" aria-modal="true" aria-label="Lưu ảnh thiệp">
                        <div className="birthday-card-save-dialog">
                            <div className="birthday-card-save-heading">
                                <h2>Ảnh thiệp đã sẵn sàng</h2>
                                <button type="button" onClick={closeSaveImage} aria-label="Đóng">×</button>
                            </div>

                            <p>Chọn mở ảnh trong tab mới, sau đó nhấn giữ vào ảnh để lưu vào Album Ảnh.</p>

                            <img className="birthday-card-save-image" src={saveImageUrl} alt="Thiệp BHS" />

                            <div className="birthday-card-save-actions">
                                <button type="button" onClick={openSaveImageInNewTab}>Mở ảnh trong tab mới</button>
                                <button type="button" onClick={closeSaveImage}>Đóng</button>
                            </div>
                        </div>
                    </div>
                )}

                {isCropOpen && (
                    <div className="birthday-card-crop-modal" role="dialog" aria-modal="true" aria-label="Crop ảnh cá nhân">
                        <div className="birthday-card-crop-dialog">
                            <div className="birthday-card-crop-heading">
                                <h2>Crop ảnh cá nhân</h2>
                                <button type="button" onClick={closeCropper} aria-label="Đóng">×</button>
                            </div>

                            <div
                                className="birthday-card-crop-frame"
                                style={cropFrameStyle}
                                onPointerDown={startCropDrag}
                                onPointerMove={moveCropDrag}
                                onPointerUp={stopCropDrag}
                                onPointerCancel={stopCropDrag}
                            />

                            <label className="birthday-card-crop-zoom">
                                Phóng to
                                <input type="range" min="1" max="3" step="0.01" value={cropZoom} onChange={updateCropZoom} />
                            </label>

                            <div className="birthday-card-crop-actions">
                                <button type="button" onClick={closeCropper}>Hủy</button>
                                <button type="button" onClick={applyCrop}>Áp dụng ảnh</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
